<template>
  <div class="trust-badge-strip">
    <div class="flex flex-wrap justify-center gap-3" :class="containerClasses">
      <div
        v-for="(badge, index) in badges"
        :key="badge.id || badge.text"
        class="trust-badge-light trust-indicator"
        :style="{ animationDelay: `${index * 0.5}s` }"
      >
        <span 
          v-if="badge.color" 
          class="w-2 h-2 rounded-full mr-2 transition-all duration-200"
          :class="[badge.color]"
        ></span>
        <span class="font-medium">{{ badge.text }}</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

/**
 * TrustBadgeStrip Component for Olvidadme
 * 
 * Implementing Trust Indicators Pattern from PATTERNS_FROM_BASKET_BASE.md
 * Reusable component for displaying trust badges with consistency across landing page
 * 
 * Features:
 * - Glassmorphism design with backdrop-filter
 * - Animated trust indicators with staggered timing
 * - Configurable badges for different contexts
 * - Professional color palette for legal authority
 * - Mobile-first responsive design
 * 
 * Applies ADR-042: Landing Page Enhancement
 * - Professional Trust Pattern implementation
 * - Reusable component architecture
 */

// Props
const props = defineProps({
  badges: {
    type: Array,
    default: () => [
      {
        id: 'gdpr',
        text: 'GDPR Compliance',
        color: 'bg-accent-500'
      },
      {
        id: 'foral',
        text: 'Derechos Forales',
        color: 'bg-accent-500'
      },
      {
        id: 'security',
        text: 'Seguridad Enterprise',
        color: 'bg-accent-500'
      }
    ]
  },
  variant: {
    type: String,
    default: 'default',
    validator: (value) => ['default', 'compact', 'prominent'].includes(value)
  },
  animated: {
    type: Boolean,
    default: true
  }
})

// Computed classes based on variant
const containerClasses = computed(() => {
  switch (props.variant) {
    case 'compact':
      return 'gap-2'
    case 'prominent':
      return 'gap-4 mb-8'
    default:
      return 'gap-3'
  }
})
</script>

<style scoped>
@reference "../../assets/styles/main.css";

.trust-badge-strip {
  @apply transition-all duration-300;
}

/* Trust indicator pulse animation */
.trust-indicator {
  animation: trustPulse 3s ease-in-out infinite;
  animation-play-state: running;
}

@keyframes trustPulse {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.02);
    opacity: 0.9;
  }
}

/* Pause animation on hover */
.trust-indicator:hover {
  animation-play-state: paused;
  transform: scale(1.05);
  opacity: 1;
}

/* Staggered trust animation delays are set via inline style */

/* Variant styles */
.trust-badge-strip .trust-badge-light {
  @apply transition-all duration-300;
}

/* Compact variant */
.trust-badge-strip.compact .trust-badge-light {
  @apply px-2 py-1 text-xs;
}

/* Prominent variant */
.trust-badge-strip.prominent .trust-badge-light {
  @apply px-4 py-2 text-base font-semibold;
  box-shadow: 0 4px 14px 0 rgba(20, 184, 166, 0.15);
}

.trust-badge-strip.prominent .trust-badge-light:hover {
  box-shadow: 0 8px 25px 0 rgba(20, 184, 166, 0.25);
  transform: translateY(-2px) scale(1.05);
}

/* Enhanced glassmorphism on hover using design system */
.trust-indicator:hover {
  backdrop-filter: var(--glass-backdrop-blur) saturate(200%);
  background: var(--glass-bg-header);
  border-color: var(--color-border-accent);
}

/* High contrast mode using design system */
@media (prefers-contrast: high) {
  .trust-badge-light {
    background: #ffffff;
    border: 2px solid var(--color-accent-600);
    color: var(--color-accent-700);
    backdrop-filter: none;
  }
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  .trust-indicator {
    animation: none;
  }
  
  .trust-indicator:hover {
    transform: none;
  }
}

/* Focus states using design system */
.trust-indicator:focus {
  outline: 2px solid var(--color-accent-primary);
  outline-offset: 2px;
}
</style>